import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container, Checkbox } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
// Reused across features rather than duplicated: the same presets + themed pickers the Score
// Backfill form uses, so a window is chosen the same way on both screens.
import { DateWindow, KNOWABLE_WINDOW_PRESETS } from '@features/scoreBackfill/DateWindow';
import { createTimeInstance } from '@utils/time';
import { isLocalhost } from '@utils/isLocalhost';
import { CompanyFilter } from './CompanyFilter';
import { FeedPicker } from './FeedPicker';
import { SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { FEED_KINDS, FEED_KIND_SEGMENTS, filterFeedsByKind } from './feedArity';
import { formatFeedId } from './format';
import { summarizeScope } from './querySummary';
import { MOCK_KIND_LABELS, MOCK_KINDS, nextMockKind } from './mockFeed';

const FieldRow = styled.div`
  margin-bottom: 28px;
`;

const SlimRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
`;

// Colors the hint through a wrapper rather than styled(Body7): BaseText hands its own
// `base-text` className to the span it renders, and a styled(Body7) class would replace it and
// then race that component's own color rule on equal specificity. Targeting .base-text from a
// parent wins outright. Same approach as SnapshotOutput's NoticeText.
const RunHintText = styled.div`
  color: ${({ theme }) => theme.themeColors.error};

  .base-text,
  .base-text * {
    color: ${({ theme }) => theme.themeColors.error};
  }
`;

const MockRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  button {
    padding: 6px 14px;
  }
`;

// The collapsed form: what was asked for, on one line, and the way back to editing it.
const SummaryRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 10px 12px;
`;

const SummaryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FORM_REGION_ID = 'feed-view-query-form';
const FEED_ID_INPUT_ID = 'feed-view-feed-id';

export const FeedViewForm = ({
  formState,
  patchForm,
  onSubmit,
  isRunning,
  feeds,
  isLoadingFeeds,
  feedsError,
  onRetryFeeds,
  isCollapsed = false,
  onExpand,
  // The company textarea's state and the resolve that empties it. Props rather than a Context read,
  // so this component stays pure and testable with nothing but props.
  companyText = '',
  onCompanyTextChange = () => {},
  isResolvingCompanies = false,
  resolveProgress = { done: 0, total: 0 },
  resolveResult = null,
  onResolveCompanies = () => {},
  onDismissUnresolved = () => {},
}) => {
  const { feedId, fromDate, toDate, companies, slim, useMock, mockKind } = formState;

  // Development-only affordance. Read per render rather than once at module scope: it's a hostname
  // comparison, and a module-scope constant can't be exercised from a test without re-importing.
  const isLocal = isLocalhost();

  const setField = patchForm;

  // The blocked-submit explanation is withheld until an attempt is actually blocked — a pristine
  // form shouldn't open by telling the analyst off for not having filled in the first field yet.
  const [hasAttempted, setHasAttempted] = React.useState(false);

  // Which kind of feed the picker suggests. HERE rather than in FeedPicker, and rather than in
  // formState, for two different reasons.
  //
  // Not in the picker: this component early-returns a collapsed summary once a result lands, so the
  // picker's whole subtree unmounts on every read. State held there would reset each time the
  // analyst ran something — which is exactly when they are most likely to be browsing for the next
  // feed. This component's instance survives the collapse, which is why hasAttempted lives here too.
  //
  // Not in formState: formState is the QUERY — the thing buildViewParams sends and the URL carries.
  // This narrows a list of suggestions. It changes no feed, no window, no company and no fidelity,
  // so putting it there would widen the query with something that never reaches the wire, and put it
  // in shared links where it would silently re-narrow somebody else's picker. Same species as the
  // table's density toggle: a preference about reading, so it survives a feed change and a reset,
  // and is not persisted across a reload.
  const [feedKind, setFeedKind] = React.useState(FEED_KINDS.all);

  // Filtered here rather than inside the picker so the picker can be told how many it is NOT being
  // shown — a count it would otherwise have no way to know, and without which "nothing here" cannot
  // explain itself.
  const visibleFeeds = React.useMemo(() => filterFeedsByKind(feeds, feedKind), [feeds, feedKind]);
  const hiddenCount = (feeds || []).length - visibleFeeds.length;

  const maxDate = createTimeInstance().toDate();

  // Both bounds are optional on this endpoint — an unset window means unbounded — so the only
  // hard requirement is a feed id.
  const validationMessage = () => {
    // The sample response is a fixture; it doesn't need a feed to read.
    if (useMock) return null;
    if (!feedId || !feedId.trim()) return 'Enter the feed id you want to view.';
    if (fromDate && toDate && createTimeInstance(fromDate).isAfter(createTimeInstance(toDate))) {
      return 'The start date must be on or before the end date.';
    }
    return null;
  };

  const invalidMessage = validationMessage();
  const canRun = invalidMessage === null && !isRunning;
  const showRunHint = hasAttempted && !canRun && !!invalidMessage;

  // The shared buttons render a plain <button> whose `disabled` is styling only, so the guard
  // lives in the handler.
  const handleSubmit = () => {
    if (!canRun) {
      setHasAttempted(true);
      return;
    }
    // No deferral and no override. Whatever is still in the company box is resolved by whoever
    // owns the query — see useCompanyResolution — so the form's job ends at "this is valid, run it".
    onSubmit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // Collapsed, the form is a line of text: the table then starts near the top of the page instead of
  // below four field blocks. Editing is one press away, and the summary states the query it stands
  // for so nothing is hidden — only folded.
  if (isCollapsed) {
    return (
      <Container bottom={16}>
        <SummaryRow>
          <SummaryText>
            <Body5 bold>{useMock ? 'Sample feed' : formatFeedId(feedId)}</Body5>
            <Body7 isLowContrast>{summarizeScope({ from: fromDate, to: toDate, companies, slim })}</Body7>
          </SummaryText>
          <TransparentFlatButton
            onClick={onExpand}
            buttonProps={{ 'aria-expanded': false, 'aria-controls': FORM_REGION_ID }}
          >
            Edit query
          </TransparentFlatButton>
        </SummaryRow>
      </Container>
    );
  }

  return (
    <div id={FORM_REGION_ID}>
      <FieldRow>
        <Container bottom={4}>
          <Body5 bold>
            <label htmlFor={FEED_ID_INPUT_ID}>Feed</label>
          </Body5>
        </Container>
        <Container bottom={12}>
          <Body7 isLowContrast>
            The feed to materialize — its id, or the name it publishes under. Any feed works: one computed by combining
            member feeds returns per-input markers, a single score feed returns its own rows and reasoning.
          </Body7>
        </Container>
        {/* A sibling of the label, never inside it: the field's accessible name is `Feed`, and a
            control nested in that label would join it — breaking every getByLabelText('Feed') and,
            more to the point, telling a screen reader the field is called something it isn't. */}
        <Container bottom={12}>
          <SegmentedControl
            label="Feed kind"
            options={FEED_KIND_SEGMENTS}
            active={feedKind}
            onSelect={setFeedKind}
            segmentWidth={104}
          />
        </Container>
        <FeedPicker
          id={FEED_ID_INPUT_ID}
          value={feedId}
          onChange={(next) => setField({ feedId: next })}
          onKeyDown={handleKeyDown}
          feeds={visibleFeeds}
          isLoading={isLoadingFeeds}
          error={feedsError}
          onRetry={onRetryFeeds}
          hiddenCount={hiddenCount}
          onShowAll={() => setFeedKind(FEED_KINDS.all)}
        />
      </FieldRow>

      <FieldRow>
        <Container bottom={4}>
          <Body5 bold>Knowable window</Body5>
        </Container>
        <Container bottom={12}>
          <Body7 isLowContrast>
            Optional. Bounds which rows come back by when they became knowable. Max is the whole history; Custom opens
            the dates.
          </Body7>
        </Container>
        <DateWindow
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={(d) => setField({ fromDate: d })}
          onToChange={(d) => setField({ toDate: d })}
          maxDate={maxDate}
          presets={KNOWABLE_WINDOW_PRESETS}
          legend="Knowable window"
        />
      </FieldRow>

      <FieldRow>
        <Container bottom={4}>
          <Body5 bold>Companies</Body5>
        </Container>
        <Container bottom={12}>
          <Body7 isLowContrast>
            Optional. Paste tickers or company ids. Leave empty to view every company in the feed.
          </Body7>
        </Container>
        <CompanyFilter
          companies={companies}
          onChange={(next) => setField({ companies: next })}
          text={companyText}
          onTextChange={onCompanyTextChange}
          onResolve={onResolveCompanies}
          isResolving={isResolvingCompanies}
          progress={resolveProgress}
          result={resolveResult}
          onDismissResult={onDismissUnresolved}
        />
      </FieldRow>

      <FieldRow>
        <SlimRow>
          <Checkbox name="slim" value={slim} onChange={() => (next) => setField({ slim: next })} />
          <div>
            <Body5 bold>Score-only rows</Body5>
            <Container top={2}>
              {/* Two losses, not one. `slim` is documented as dropping the per-input markers AND the
                  single-input payload columns — the score's declared extra_columns, its rationale and
                  its thinking — so naming only the markers undersells it to whoever is reading a
                  score feed, where the markers were never there and the payload is the whole point. */}
              <Body7 isLowContrast>
                Drops the per-input markers and each row’s written reasoning, for a cheaper payload. Leave off to see
                what went into — or was missing from — a combined row, and why a score feed scored what it did.
              </Body7>
            </Container>
          </div>
        </SlimRow>
      </FieldRow>

      {isLocal && (
        <MockRow>
          <SkeletonButton onClick={() => setField({ useMock: !useMock })} buttonProps={{ 'aria-pressed': !!useMock }}>
            Use sample feed
          </SkeletonButton>
          {useMock && (
            <SkeletonButton
              onClick={() => setField({ mockKind: nextMockKind(mockKind) })}
              buttonProps={{ 'aria-label': 'Switch sample feed kind' }}
            >
              {MOCK_KIND_LABELS[mockKind] || MOCK_KIND_LABELS[MOCK_KINDS.MULTI]}
            </SkeletonButton>
          )}
          <Body7 isLowContrast>
            {useMock
              ? 'View feed will load a sample response instead of calling the API.'
              : 'Local development only — loads a sample response with no API call.'}
          </Body7>
        </MockRow>
      )}

      {/* No aria-disabled / aria-describedby here: FlatButton destructures only children,
          buttonProps, disabled, onClick and fullWidth, and BaseButton in turn keeps only
          children, onClick and disabled — so both a direct aria prop and one routed through
          buttonProps are dropped before reaching the <button>. The hint below carries
          role="alert" instead, which announces it on appearance without needing the association. */}
      <FlatButton onClick={handleSubmit} fullWidth>
        {isRunning ? 'Loading feed…' : useMock ? 'View sample feed' : 'View feed'}
      </FlatButton>
      {showRunHint && (
        <Container top={8}>
          <RunHintText role="alert">
            <Body7>{invalidMessage}</Body7>
          </RunHintText>
        </Container>
      )}
    </div>
  );
};
